import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { useState } from 'react';
import SkeletonBox from 'conversations-skeleton-state/components/SkeletonBox';
import SkeletonText from 'conversations-skeleton-state/components/SkeletonText';
import ErrorMessageWithRetry from '../../shared/components/ErrorMessageWithRetry';
import { getAwayMode } from '../operators/getters';
import { useAgentStatus } from '../hooks/useAgentStatus';
import AgentStatusPrompt from './AgentStatusPrompt';
import AgentStatusToggle from './AgentStatusToggle';
import { useRealtimeAgentStatus } from '../hooks/useRealtimeAgentStatus';
import { TrackUsageContext, usageTrackerAdapter } from '../../usage-tracking/contexts/TrackUsageContext';
import { EndOutOfOfficePrompt } from './EndOutOfOfficePrompt';
import { useOutOfOfficeHours } from '../../working-hours/hooks/useOutOfOfficeHours';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AgentStatus = ({
  agentId,
  // TODO: replace by moving capacity limits fetchers/hook into routing-ui
  atCapacity,
  // TODO: replace by moving SetAgentStatusPrompt and contextual-prompts fetchers into routing-ui
  Prompt,
  usageTracker,
  hasAvailabilityStatusEditAccess,
  isUngatedForHelpDeskRoutingScopeFiltering,
  isUngatedForWorkingHours,
  isUngatedForOutOfOffice,
  workspace
}) => {
  const {
    agentStatus,
    error,
    loadingAgentStatus,
    refetchAgentStatus,
    toggleAwayMode: updateAwayMode,
    updateAwayModeError
  } = useAgentStatus(usageTracker, workspace);
  useRealtimeAgentStatus(agentId, workspace);
  const awayModeState = agentStatus && getAwayMode(agentStatus);
  const {
    currentSegment,
    isLoadingOOOHours
  } = useOutOfOfficeHours(agentId, {
    isViewingSelf: true
  });
  const [showConfirmEndOutOfOffice, setShowConfirmEndOutOfOffice] = useState(false);
  if (loadingAgentStatus || isLoadingOOOHours) {
    return /*#__PURE__*/_jsxs("span", {
      className: "flex-row width-100 align-center justify-between p-x-2",
      children: [/*#__PURE__*/_jsx(SkeletonText, {
        width: "100px"
      }), /*#__PURE__*/_jsx(SkeletonBox, {
        height: "26px",
        width: "52px"
      })]
    });
  }
  if (error || !agentStatus) {
    return /*#__PURE__*/_jsx(ErrorMessageWithRetry, {
      title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "routing-ui.agent-status.loadError"
      }),
      retry: refetchAgentStatus
    });
  }
  if (updateAwayModeError) {
    return /*#__PURE__*/_jsx(ErrorMessageWithRetry, {
      title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "routing-ui.agent-status.updateError"
      }),
      retry: () => updateAwayMode(!awayModeState)
    });
  }
  return /*#__PURE__*/_jsxs(TrackUsageContext.Provider, {
    value: usageTrackerAdapter(usageTracker),
    children: [showConfirmEndOutOfOffice && /*#__PURE__*/_jsx(EndOutOfOfficePrompt, {
      userId: agentId,
      isViewingSelf: true,
      onConfirm: () => {
        updateAwayMode(false);
        setShowConfirmEndOutOfOffice(false);
      },
      onReject: () => setShowConfirmEndOutOfOffice(false)
    }), /*#__PURE__*/_jsx(AgentStatusPrompt, {
      agentStatus: agentStatus,
      Prompt: Prompt,
      children: /*#__PURE__*/_jsx(AgentStatusToggle, {
        agentStatus: agentStatus,
        updateAwayMode: awayMode => {
          if (isUngatedForOutOfOffice && currentSegment && !awayMode) {
            setShowConfirmEndOutOfOffice(true);
          } else {
            updateAwayMode(awayMode);
          }
        },
        atCapacity: atCapacity,
        hasAvailabilityStatusEditAccess: hasAvailabilityStatusEditAccess,
        isUngatedForHelpDeskRoutingScopeFiltering: isUngatedForHelpDeskRoutingScopeFiltering,
        isUngatedForWorkingHours: isUngatedForWorkingHours,
        isUngatedForOutOfOffice: isUngatedForOutOfOffice,
        userId: agentId
      })
    })]
  });
};
AgentStatus.displayName = 'AgentStatus';
export default AgentStatus;